// src/services/gear.ts
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { uploadImage } from './imageUpload';
import type { TackleBoxItem } from '../types';

export const GEAR_SUBCOLLECTION = 'gear';
export const EQUIPPED_GEAR_EVENT = 'roddo:equippedGearChanged';
const EQUIPPED_GEAR_CACHE_PREFIX = 'roddo.equippedGear.';
const LOCAL_SNAPSHOT_GRACE_MS = 15_000;

export type GearDoc = Omit<TackleBoxItem, 'id'> & {
  id?: string;
  createdAt?: number;
  updatedAt?: number;
};

type EquippedGearShape = {
  rodId?: string | null;
  reelId?: string | null;
  lureId?: string | null; // legacy alias
  lureOrBaitId?: string | null; // canonical
  otherId?: string | null;
  useTackleBoxSets?: boolean;
  updatedAt?: number | null;
};

const userGearCollection = (uid: string) =>
  collection(db, 'users', uid, GEAR_SUBCOLLECTION);

function stripUndefined<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as T;
}

function normalizeGearItem(data: GearDoc, fallbackId: string): TackleBoxItem {
  return {
    ...(data as TackleBoxItem),
    id: data.id || fallbackId,
  };
}

function normalizeEquippedGear(input: any): EquippedGearShape {
  const lureOrBaitId = input?.lureOrBaitId ?? input?.lureId ?? null;
  return {
    rodId: input?.rodId ?? null,
    reelId: input?.reelId ?? null,
    lureOrBaitId,
    lureId: input?.lureId ?? lureOrBaitId ?? null,
    otherId: input?.otherId ?? null,
    useTackleBoxSets: !!input?.useTackleBoxSets,
    updatedAt: typeof input?.updatedAt === 'number' ? input.updatedAt : null,
  };
}

function equippedCacheKey(uid: string): string {
  return `${EQUIPPED_GEAR_CACHE_PREFIX}${uid}`;
}

function readCachedEquippedGear(uid: string): EquippedGearShape | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(equippedCacheKey(uid));
    return raw ? normalizeEquippedGear(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeCachedEquippedGear(uid: string, equipped: EquippedGearShape): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(equippedCacheKey(uid), JSON.stringify(normalizeEquippedGear(equipped)));
  } catch {
    // Non-fatal: private mode / storage quota should not break equip writes.
  }
}

function emitEquippedGear(uid: string, equipped: EquippedGearShape): void {
  const normalized = normalizeEquippedGear(equipped);
  writeCachedEquippedGear(uid, normalized);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(EQUIPPED_GEAR_EVENT, {
        detail: { uid, equipped: normalized },
      }),
    );
  }
}

function buildEquippedPayload(equipped: Partial<EquippedGearShape>): Record<string, any> {
  // IMPORTANT: setDoc(..., { merge: true }) does NOT treat dotted string keys
  // like updateDoc does. Keep this as a nested object so hard refresh reads
  // users/{uid}.equippedGear correctly instead of creating literal fields like
  // "equippedGear.rodId".
  return {
    equippedGear: stripUndefined({
      rodId: equipped.rodId,
      reelId: equipped.reelId,
      lureOrBaitId: equipped.lureOrBaitId,
      lureId: equipped.lureId ?? equipped.lureOrBaitId,
      otherId: equipped.otherId,
      useTackleBoxSets: equipped.useTackleBoxSets,
      updatedAt: equipped.updatedAt,
    }),
  };
}

function buildEquippedSlotPayload(
  slot: 'rodId' | 'reelId' | 'lureOrBaitId',
  gearId: string | null,
  updatedAt: number,
): Record<string, any> {
  if (slot === 'lureOrBaitId') {
    return buildEquippedPayload({
      lureOrBaitId: gearId,
      lureId: gearId, // legacy alias sync
      updatedAt,
    });
  }

  return buildEquippedPayload({
    [slot]: gearId,
    updatedAt,
  } as Partial<EquippedGearShape>);
}

function mergeEquippedSlot(
  current: EquippedGearShape | null,
  slot: 'rodId' | 'reelId' | 'lureOrBaitId',
  gearId: string | null,
  updatedAt: number,
): EquippedGearShape {
  const next = normalizeEquippedGear(current || {});
  if (slot === 'lureOrBaitId') {
    next.lureOrBaitId = gearId;
    next.lureId = gearId;
  } else {
    next[slot] = gearId;
  }
  next.updatedAt = updatedAt;
  return next;
}

async function clearEquippedReferencesIfNeeded(uid: string, gearId: string): Promise<void> {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) return;

  const equipped = normalizeEquippedGear((userSnap.data() as any)?.equippedGear || {});
  const updatedAt = Date.now();
  const nextEquipped = normalizeEquippedGear(equipped);
  let changed = false;

  if ((equipped.rodId ?? null) === gearId) {
    nextEquipped.rodId = null;
    changed = true;
  }

  if ((equipped.reelId ?? null) === gearId) {
    nextEquipped.reelId = null;
    changed = true;
  }

  const equippedLureOrBaitId = equipped.lureOrBaitId ?? equipped.lureId ?? null;
  if (equippedLureOrBaitId === gearId || equipped.lureId === gearId) {
    nextEquipped.lureOrBaitId = null;
    nextEquipped.lureId = null;
    changed = true;
  }

  if (changed) {
    nextEquipped.updatedAt = updatedAt;
    emitEquippedGear(uid, nextEquipped);
    await setDoc(userRef, buildEquippedPayload(nextEquipped), { merge: true });
  }
}

export async function addGearItem(
  uid: string,
  data: Omit<TackleBoxItem, 'id'>,
): Promise<string> {
  const cleanedData = stripUndefined({
    ...data,
    value:
      typeof data.value === 'number' && Number.isFinite(data.value)
        ? data.value
        : undefined,
    weightOz:
      typeof data.weightOz === 'number' && Number.isFinite(data.weightOz)
        ? data.weightOz
        : undefined,
  });

  const docRef = await addDoc(userGearCollection(uid), {
    ...cleanedData,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  } satisfies GearDoc);

  await setDoc(
    docRef,
    {
      id: docRef.id,
    },
    { merge: true },
  );

  return docRef.id;
}

export async function updateGearItem(
  uid: string,
  gearId: string,
  patch: Partial<TackleBoxItem>,
): Promise<void> {
  const docRef = doc(db, 'users', uid, GEAR_SUBCOLLECTION, gearId);

  const cleanedPatch = stripUndefined({
    ...patch,
    value:
      typeof patch.value === 'number' && Number.isFinite(patch.value)
        ? patch.value
        : patch.value === undefined
          ? undefined
          : null,
    weightOz:
      typeof patch.weightOz === 'number' && Number.isFinite(patch.weightOz)
        ? patch.weightOz
        : patch.weightOz === undefined
          ? undefined
          : null,
  });

  delete (cleanedPatch as any).id;

  await setDoc(
    docRef,
    {
      ...cleanedPatch,
      updatedAt: Date.now(),
    },
    { merge: true },
  );
}

export async function deleteGearItem(uid: string, gearId: string): Promise<void> {
  await clearEquippedReferencesIfNeeded(uid, gearId);

  const docRef = doc(db, 'users', uid, GEAR_SUBCOLLECTION, gearId);
  await deleteDoc(docRef);
}

export function subscribeToGear(
  uid: string,
  onChange: (items: TackleBoxItem[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(userGearCollection(uid), orderBy('createdAt', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) =>
        normalizeGearItem(docSnap.data() as GearDoc, docSnap.id),
      );
      onChange(items);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[gear] subscribeToGear error', err);
    },
  );
}

export function subscribeToEquippedGear(
  uid: string,
  onChange: (equipped: EquippedGearShape) => void,
  onError?: (err: unknown) => void,
): () => void {
  const userDocRef = doc(db, 'users', uid);
  let disposed = false;

  const push = (equipped: EquippedGearShape) => {
    if (!disposed) onChange(normalizeEquippedGear(equipped));
  };

  const cached = readCachedEquippedGear(uid);
  if (cached) queueMicrotask(() => push(cached));

  const onLocalEquipped = (event: Event) => {
    const detail = (event as CustomEvent)?.detail || {};
    if (detail.uid === uid) push(detail.equipped || {});
  };

  if (typeof window !== 'undefined') {
    window.addEventListener(EQUIPPED_GEAR_EVENT, onLocalEquipped as EventListener);
  }

  const unsubscribeSnapshot = onSnapshot(
    userDocRef,
    (snapshot) => {
      const incoming = normalizeEquippedGear((snapshot.data() as any)?.equippedGear || {});
      const latestCached = readCachedEquippedGear(uid);
      const incomingAt = incoming.updatedAt || 0;
      const cachedAt = latestCached?.updatedAt || 0;

      // Firestore can briefly deliver the previous user-doc state after a local equip click.
      // During that small window, keep the newer local/cached equip state instead of snapping back.
      if (latestCached && cachedAt > incomingAt && Date.now() - cachedAt < LOCAL_SNAPSHOT_GRACE_MS) {
        push(latestCached);
        return;
      }

      writeCachedEquippedGear(uid, incoming);
      push(incoming);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[gear] subscribeToEquippedGear error', err);
    },
  );

  return () => {
    disposed = true;
    unsubscribeSnapshot();
    if (typeof window !== 'undefined') {
      window.removeEventListener(EQUIPPED_GEAR_EVENT, onLocalEquipped as EventListener);
    }
  };
}

export async function fetchGear(uid: string): Promise<TackleBoxItem[]> {
  const q = query(userGearCollection(uid), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);

  return snap.docs.map((docSnap) =>
    normalizeGearItem(docSnap.data() as GearDoc, docSnap.id),
  );
}

export async function uploadGearImage(
  uid: string,
  gearId: string,
  file: File,
): Promise<string> {
  const url = await uploadImage(file, `gear/${uid}/${gearId}.jpg`, {
    maxSizeMB: 1.2,
    maxDim: 1400,
    contentType: 'image/jpeg',
  });

  await updateGearItem(uid, gearId, { imageUrl: url });
  return url;
}

export async function setEquippedGearPatch(
  uid: string,
  patch: Partial<EquippedGearShape>,
): Promise<void> {
  const userDocRef = doc(db, 'users', uid);
  const updatedAt = Date.now();
  const cached = readCachedEquippedGear(uid);
  const hasLureOrBait = Object.prototype.hasOwnProperty.call(patch, 'lureOrBaitId');
  const hasLure = Object.prototype.hasOwnProperty.call(patch, 'lureId');
  const lureOrBaitId = hasLureOrBait
    ? patch.lureOrBaitId ?? null
    : hasLure
      ? patch.lureId ?? null
      : cached?.lureOrBaitId ?? cached?.lureId ?? null;
  const lureId = hasLure
    ? patch.lureId ?? null
    : hasLureOrBait
      ? patch.lureOrBaitId ?? null
      : cached?.lureId ?? cached?.lureOrBaitId ?? null;

  const optimisticEquipped = normalizeEquippedGear({
    ...(cached || {}),
    ...patch,
    lureOrBaitId,
    lureId,
    updatedAt,
  });

  emitEquippedGear(uid, optimisticEquipped);
  await setDoc(userDocRef, buildEquippedPayload(optimisticEquipped), { merge: true });
  emitEquippedGear(uid, optimisticEquipped);
}

export async function setEquippedGearSlot(
  uid: string,
  slot: 'rodId' | 'reelId' | 'lureOrBaitId',
  gearId: string | null,
): Promise<void> {
  const userDocRef = doc(db, 'users', uid);
  const updatedAt = Date.now();
  const cached = readCachedEquippedGear(uid);
  const optimisticEquipped = mergeEquippedSlot(cached, slot, gearId, updatedAt);

  // Instant cross-view update. Firestore remains source of truth and will confirm shortly after.
  emitEquippedGear(uid, optimisticEquipped);

  await setDoc(userDocRef, buildEquippedSlotPayload(slot, gearId, updatedAt), { merge: true });

  // Re-broadcast after the write resolves so newly mounted views receive the same confirmed target.
  emitEquippedGear(uid, optimisticEquipped);
}
